import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import { QueryTag, SearchParam } from '@common/ts';
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  IconArrowUp,
  IconArrowDown,
  IconArrowsUpDown,
  IconAdjustmentsHorizontal,
  IconLoader2,
} from '@tabler/icons-react';
import { getErrorMessage } from '@common/utils';
import { ChevronDown } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import { toast } from 'react-toastify';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LangContext } from '@/context/LangProvider';
import { QuestionAnswersColumns } from './QuestionAnswersColumns';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import DataTablePagination from '../paginations/DataTablePagination';
import {
  useGetInterviewQuestionTransripts,
  useGetTranslationLanguages,
  useGetTranslationProgress,
} from '@/reactQuery/get';
import { useMutateGenerateTranscriptsTranslation } from '@/reactQuery/post';
import { useMutateDeleteTranslation } from '@/reactQuery/delete';
import { Category, CategoryApiResponse, SelectedCategory } from '@/types';
import useCategoryStore from '@/store/CategoryStore';
import TranscriptsTranslationDropdown from '@/pages/projects/single-project/Content/transcripts_table_data/TranscriptsTranslationDropdown';
import { getSortedTranslatedLanguages } from '@/utils/getSortedTranslatedLanguages';
import languagesList from '@/lang/languages/languagesList';
import useTranslationsLoadingStore from '@/store/TranslationLoadingStore';

interface QuestionAnswersTableProps {
  questionID: string | undefined;
  selectedCategories: SelectedCategory[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<SelectedCategory[]>>;
  categories?: CategoryApiResponse;
  isPendingInterviewQuestions: boolean;
}

export default function QuestionAnswersTable({
  selectedCategories,
  setSelectedCategories,
  questionID,
  categories,
  isPendingInterviewQuestions,
}: QuestionAnswersTableProps) {
  const { project_id: projectId, workspace_id: workspaceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterId = searchParams.get('filter_id');
  const categoryType = useCategoryStore((state) => state.categoryType);
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();

  // Translation loading store
  const { translatingProjects, setTranslatingProjects } =
    useTranslationsLoadingStore();

  // Check if current project is translating - memoized to prevent infinite re-renders
  const currentProjectTranslating = useMemo(
    () => translatingProjects?.find((p) => p.projectId === projectId && p.source === 'questions'),
    [translatingProjects, projectId],
  );
  const isTranslating = !!currentProjectTranslating;

  // Translation state — derived from Zustand store and URL (no local state)
  const pendingLanguage = currentProjectTranslating?.language || null;
  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);
  const activeLanguage = translationLanguage || 'Original';

  // Fetch available translation languages
  const { data: languages = [], refetch: refetchLanguages } = useGetTranslationLanguages(projectId);
  const translatedLanguages = ['Original', ...languages];
  const unTranslatedLanguages = languagesList.language
    .map(({ label }) => label)
    .filter((language) => !languages.includes(language) && language !== 'Original');

  // Translation mutation
  const { mutateAsync: triggerTranslation } = useMutateGenerateTranscriptsTranslation(projectId);
  const { mutateAsync: deleteTranslation } = useMutateDeleteTranslation(projectId);

  // Poll translation progress via Tanstack Query (auto-enabled when pendingLanguage is set)
  const { data: progressData, dataUpdatedAt: progressUpdatedAt } = useGetTranslationProgress(projectId, pendingLanguage);
  const isCompletingRef = useRef(false);
  const lastCountRef = useRef<number>(0);
  const stableCountRef = useRef<number>(0);
  const translationStartTimeRef = useRef<number>(0);
  const STABLE_POLLS_THRESHOLD = 3; // 3 consecutive polls at same value (~15s) = complete
  const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes max

  // Track translation start time and reset stability counters when translation begins/ends
  useEffect(() => {
    if (isTranslating && !translationStartTimeRef.current) {
      translationStartTimeRef.current = Date.now();
      lastCountRef.current = 0;
      stableCountRef.current = 0;
    }
    if (!isTranslating) {
      translationStartTimeRef.current = 0;
    }
  }, [isTranslating]);

  // Clear invalid language from URL on mount (e.g. language was deleted in another tab)
  useEffect(() => {
    if (translationLanguage && !translatedLanguages.includes(translationLanguage) && !isTranslating) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
      setSearchParams(newSearchParams);
    }
  }, [translationLanguage, translatedLanguages, isTranslating]);

  // React to progress changes — use stability detection instead of expected total comparison.
  // The backend translates ALL conversations in the project, not just those visible for this
  // question, so we cannot rely on matching an exact count. Instead, we complete when the
  // translated count is greater than zero and has not changed for STABLE_POLLS_THRESHOLD
  // consecutive polls, or when the 5-minute timeout elapses.
  useEffect(() => {
    if (!pendingLanguage || !isTranslating || !progressData || isCompletingRef.current) return;

    const translatedCount = progressData.count;

    // Timeout check — abort if translation is taking too long
    const elapsed = Date.now() - translationStartTimeRef.current;
    if (elapsed >= TIMEOUT_MS) {
      setTranslatingProjects((prev) => prev?.filter((p) => !(p.projectId === projectId && p.source === 'questions')) || []);
      toast.error(
        `Translation to ${pendingLanguage} is taking longer than expected. Please try again or contact support if the issue persists.`,
      );
      return;
    }

    // Stability detection: count > 0 and unchanged for STABLE_POLLS_THRESHOLD consecutive polls
    if (translatedCount > 0 && translatedCount === lastCountRef.current) {
      stableCountRef.current++;
    } else {
      stableCountRef.current = 0;
    }
    lastCountRef.current = translatedCount;

    if (translatedCount > 0 && stableCountRef.current >= STABLE_POLLS_THRESHOLD) {
      isCompletingRef.current = true;

      const completeTranslation = async () => {
        try {
          await Promise.all([
            refetchLanguages(),
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.QUESTION_TRANSCRIPTS(
                filterId,
                categoryType,
                projectId,
                questionID,
                pendingLanguage,
              ),
            }),
            // Invalidate conversations cache for cross-tab consistency (TranscriptsTable view)
            queryClient.invalidateQueries({ queryKey: [QueryTag.CONVERSATIONS] }),
          ]);

          toast.success(`Translation to ${pendingLanguage} completed!`);
        } finally {
          // Clear loading state in finally so the spinner clears even if invalidation rejects
          setTranslatingProjects((prev) => prev?.filter((p) => !(p.projectId === projectId && p.source === 'questions')) || []);
          isCompletingRef.current = false;
        }
      };

      completeTranslation();
    }
    // progressUpdatedAt changes on every successful refetch (even when data is structurally
    // identical), ensuring this effect re-fires despite TanStack Query's structural sharing.
  }, [progressData, progressUpdatedAt, pendingLanguage, isTranslating]);

  // Determine which language param to pass to API
  const languageParam = activeLanguage === 'Original' ? null : activeLanguage;

  const {
    data: selectedQuestionTranscripts,
    error,
    isLoading: isTranscriptsLoading,
  } = useGetInterviewQuestionTransripts(
    filterId,
    categoryType,
    projectId,
    questionID,
    languageParam,
  );

  const isReady = !isPendingInterviewQuestions && !isTranscriptsLoading;

  useEffect(() => {
    if (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }
  }, [error]);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const { lang } = useContext(LangContext);
  const [areAllCategoriesZeroSingle, setAreAllCategoriesZeroSingle] = useState(false);
  const [areAllCategoriesZeroMulti, setAreAllCategoriesZeroMulti] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  // Debounced search for backend integration
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebounce(searchInput, 500);

  const transformedCategories = useMemo(
    () =>
      categories?.categories?.map((category: { id: string; label: string }) => ({
        label: category?.label,
        value: category?.id,
      })) || [],
    [categories],
  );

  const areAllCountsZeroCheck = useCallback(() => {
    if (!categories) {
      return;
    }
    // Check if all counts in 'categories' are 0
    const allCategoriesZero = categories.categories?.every(
      (category: Category) => 'count' in category && category.count === 0,
    );
    // Check if all counts in 'categories_multi' are 0
    const allCategoriesMultiZero = categories?.categories_multi?.every(
      (category: Category) => 'count' in category && category.count === 0,
    );

    // Set state based on whether all counts are zero
    setAreAllCategoriesZeroSingle(allCategoriesZero);
    setAreAllCategoriesZeroMulti(allCategoriesMultiZero);
  }, [categories, setAreAllCategoriesZeroSingle]);

  const isCategoryDataExists =
    categories?.categories?.length !== 0 || categories?.categories_multi?.length !== 0;
  const categoryDataSingle = categories?.categories;
  const categoryDataMultiple = categories?.categories_multi;

  // Filter logic
  const filteredTranscripts = useMemo(() => {
    if (selectedCategories?.length === 0) {
      return selectedQuestionTranscripts;
    }

    return selectedQuestionTranscripts?.filter((transcript) => {
      // Normalize transcript.category to be an array
      const transcriptCategories = Array.isArray(transcript?.category)
        ? transcript?.category
        : [transcript?.category];

      const selectedCategoryLabels = selectedCategories?.map((category) =>
        category?.label?.toLowerCase(),
      );

      const transcriptCategoryLabels = transcriptCategories?.map((cat) =>
        cat?.label?.toLowerCase(),
      );

      return transcriptCategoryLabels.some((label) => selectedCategoryLabels?.includes(label));
    });
  }, [selectedQuestionTranscripts, selectedCategories]);

  const columns = QuestionAnswersColumns(
    categoryDataSingle,
    questionID,
    categoryDataMultiple,
    isCategoryDataExists,
    areAllCategoriesZeroSingle,
    areAllCategoriesZeroMulti,
    transformedCategories,
    setSelectedCategories,
    selectedCategories,
    projectId,
    workspaceId,
  );

  const table = useReactTable({
    data: filteredTranscripts || [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  useEffect(() => {
    setColumnFilters(debouncedSearch ? [{ id: 'questions', value: debouncedSearch }] : []);
    table.setPageIndex(0);
  }, [debouncedSearch, table]);

  useEffect(() => {
    areAllCountsZeroCheck();
  }, [categories, areAllCountsZeroCheck]);

  // Handle language selection
  const handleLanguageSelection = async (language: string) => {
    // Prevent multiple simultaneous translations
    if (isTranslating && currentProjectTranslating) {
      toast.warning(
        `Translation to ${currentProjectTranslating.language} is already in progress. Please wait.`,
      );
      return;
    }

    if (translatedLanguages.includes(language)) {
      // Already translated - just switch via URL
      const newSearchParams = new URLSearchParams(searchParams);
      if (language === 'Original') {
        newSearchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
      } else {
        newSearchParams.set(SearchParam.TRANSLATION_LANGUAGE, language);
      }
      setSearchParams(newSearchParams);
    } else {
      // Need to trigger translation
      try {
        const conversationCount = selectedQuestionTranscripts?.length || 0;

        // Set URL param and loading state
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set(SearchParam.TRANSLATION_LANGUAGE, language);
        setSearchParams(newSearchParams);

        setTranslatingProjects((prev) => [
          ...(prev || []),
          {
            projectId: projectId!,
            language,
            length: conversationCount,
            source: 'questions',
          },
        ]);

        toast.info(`Translation to ${language} initiated. This may take a few minutes...`);

        await triggerTranslation({ languages: [language] });
      } catch (translationError) {
        const errorMessage = getErrorMessage(translationError);
        toast.error(`Translation failed: ${errorMessage}`);
        // Clean up loading state and URL on error
        const errorSearchParams = new URLSearchParams(searchParams);
        errorSearchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
        setSearchParams(errorSearchParams);
        setTranslatingProjects((prev) => prev?.filter((p) => !(p.projectId === projectId && p.source === 'questions')) || []);
      }
    }
  };

  // Handle language deletion
  const handleDeleteLanguage = async (language: string) => {
    try {
      toast.info(`Deleting ${language} translations...`);
      await deleteTranslation({ language });
      toast.success(`${language} translations deleted successfully`);

      // Refetch languages to update the dropdown
      await refetchLanguages();

      // If we were viewing the deleted language, switch back to Original via URL
      if (activeLanguage === language) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
        setSearchParams(newSearchParams);
      }
    } catch (deletionError) {
      const errorMessage = getErrorMessage(deletionError);
      toast.error(`Failed to delete ${language} translations: ${errorMessage}`);
    }
  };

  if (!isReady) {
    return (
      <div className="flex items-center justify-center w-full py-20 mx-auto">
        <IconLoader2 style={{ color: currentTheme?.primary }} className="animate-spin" size={35} />
      </div>
    );
  }

  return (
    <div className="w-full mt-4">
      {/* Language and View controls - aligned with tabs */}
      <div className="flex items-center justify-end w-full mb-3 gap-2">
        <TranscriptsTranslationDropdown
          isLoading={isTranslating}
          activeLanguage={pendingLanguage || activeLanguage}
          handleLanguageSelection={handleLanguageSelection}
          translatedLanguages={getSortedTranslatedLanguages(['Original'], translatedLanguages)}
          unTranslatedLanguages={unTranslatedLanguages}
          onDeleteLanguage={handleDeleteLanguage}
          pendingLanguage={pendingLanguage}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              <IconAdjustmentsHorizontal size={16} className="mr-2" />
              View
              <ChevronDown className="w-4 h-4 ml-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => (
                <DropdownMenuCheckboxItem
                  key={column?.id}
                  className="capitalize"
                  checked={column.getIsVisible()}
                  onCheckedChange={(value) => column.toggleVisibility(!!value)}
                >
                  {column?.id}
                </DropdownMenuCheckboxItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="border rounded-md">
        <Table className="text-dark-text">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    <div className="flex items-center w-full gap-2">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header?.id !== 'actions' && header?.id !== 'category' && (
                        <div>
                          {{
                            asc: <IconArrowUp size={18} />,
                            desc: <IconArrowDown size={18} />,
                          }[header.column.getIsSorted() as string] ?? (
                            <IconArrowsUpDown size={18} />
                          )}
                        </div>
                      )}
                      {header?.id === 'category' && (
                        <div className="self-start mt-3 -ml-6">
                          {{
                            asc: <IconArrowUp size={18} />,
                            desc: <IconArrowDown size={18} />,
                          }[header.column.getIsSorted() as string] ?? (
                            <IconArrowsUpDown size={18} />
                          )}
                        </div>
                      )}
                    </div>
                    {header?.id === 'questions' && (
                      <Input
                        placeholder="Search transcripts..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="w-full mb-2.5 min-w-52 max-w-52 bg-white"
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table?.getRowModel()?.rows?.length ? (
              table?.getRowModel()?.rows?.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns?.length} className="h-24 text-center">
                  {lang.get('msg.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
